// Notification channels (PHASE25): where SLA breach and recovery notices go (e-mail addresses or a webhook),
// a test button per channel, and the log of notices sent.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Send, Trash2, Pencil } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { PageHeader, DataTable, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { fmtTime } from '@/lib/utils';
import { useRefOptions } from '@/hooks/meta';
import { t } from '@/i18n';

interface Channel {
  id: number; name: string; type: 'EMAIL' | 'WEBHOOK'; target: string; has_secret: boolean; events: string; queue_ids: string | null; status: string;
  last_sent_at: string | null; last_error: string | null;
}
const EVENT_LABEL: Record<string, string> = { 'sla.breach': 'SLA breach', 'sla.recovered': 'SLA recovered', test: 'Test' };
const eventText = (e: string) => (EVENT_LABEL[e] ? t(EVENT_LABEL[e]) : e);

export function NotificationsPage() {
  const q = useQuery({ queryKey: ['notification-channels'], queryFn: () => api<{ rows: Channel[] }>('/notification-channels') });
  const log = useQuery({ queryKey: ['notification-log'], queryFn: () => api<{ rows: any[] }>('/notification-log', { query: { from: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 16) } }) });
  const canAdd = useCan('notifications.add'), canEdit = useCan('notifications.edit'), canDelete = useCan('notifications.delete');
  const [editing, setEditing] = React.useState<Channel | 'new' | null>(null);
  const queues = useRefOptions('queues');
  const qname = (id: string) => (queues.data ?? []).find((x) => String(x.id) === id)?.name ?? `#${id}`;
  const refresh = () => { q.refetch(); log.refetch(); };

  async function test(c: Channel) {
    try {
      const r = await api<{ ok: boolean; message?: string }>(`/notification-channels/${c.id}/test`, { method: 'POST' });
      if (r.ok) toast.ok(t('Test notice sent to {{name}}', { name: c.name })); else toast.error(`${c.name}: ${r.message}`);
    } catch (e) { toast.error(errorText(e)); }
    refresh();
  }
  async function remove(c: Channel) {
    if (!confirm(t('Remove {{name}}? It will get no more notices.', { name: c.name }))) return;
    try { await api(`/notification-channels/${c.id}`, { method: 'DELETE' }); toast.ok(t('Removed')); refresh(); } catch (e) { toast.error(errorText(e)); }
  }

  return (
    <div className="grid gap-4">
      <PageHeader title={t('Notifications')} description={t('Where SLA breach and recovery notices go. The dashboard shows open breaches to supervisors either way.')}>
        {canAdd && <Button onClick={() => setEditing('new')}><Plus /> {t('Add channel')}</Button>}
      </PageHeader>
      <ErrorBox error={q.error} />
      <Card>
        <CardHeader><CardTitle>{t('Channels')}</CardTitle></CardHeader>
        <CardContent>
          <DataTable rowKey={(r) => r.id} rows={q.data?.rows ?? []} empty={t('No channels yet: breaches show on the dashboard only.')} columns={[
            { key: 'name', label: t('Name'), render: (r) => <span className="font-medium">{r.name}</span> },
            { key: 'type', label: t('Type'), render: (r) => <Badge variant="outline">{r.type === 'EMAIL' ? t('E-mail') : t('Webhook')}</Badge> },
            { key: 'target', label: t('Sends to'), render: (r) => <span className="break-all text-sm">{r.target}{r.has_secret && <span className="ml-1 text-xs text-muted-foreground">{t('(signed)')}</span>}</span> },
            { key: 'queue_ids', label: t('Queues'), render: (r) => r.queue_ids ? r.queue_ids.split(',').map(qname).join(', ') : t('All') },
            { key: 'events', label: t('Events'), render: (r) => r.events.split(',').map(eventText).join(', ') },
            { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
            { key: 'last_sent_at', label: t('Last notice'), render: (r) => <span className="grid text-xs">
              {r.last_sent_at ? fmtTime(r.last_sent_at) : '-'}
              {r.last_error && <span className="text-destructive" data-testid={`channel-error-${r.id}`}>{r.last_error}</span>}</span> }]}
            actions={(r) => <div className="flex justify-end gap-1">
              {canEdit && <Button size="sm" variant="outline" onClick={() => test(r)} aria-label={t('Test {{name}}', { name: r.name })}><Send /> {t('Test')}</Button>}
              {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(r)} aria-label={t('Edit {{name}}', { name: r.name })}><Pencil /></Button>}
              {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(r)} aria-label={t('Remove {{name}}', { name: r.name })}><Trash2 className="text-destructive" /></Button>}
            </div>} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t('Notices sent (last 7 days)')}</CardTitle></CardHeader>
        <CardContent>
          <div data-testid="notification-log">
            <DataTable rowKey={(r) => r.id} rows={log.data?.rows ?? []} empty={t('No notices yet.')} columns={[
              { key: 'created_at', label: t('Time'), render: (r) => fmtTime(r.created_at) }, { key: 'channel_name', label: t('Channel') },
              { key: 'event', label: t('Event'), render: (r) => eventText(r.event) },
              { key: 'status', label: t('Result'), render: (r) => <StatusBadge value={r.status} /> },
              { key: 'error', label: t('Error'), render: (r) => <span className="text-xs text-destructive">{r.error ?? ''}</span> }]} />
          </div>
        </CardContent>
      </Card>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <DialogContent title={editing === 'new' ? t('Add channel') : t('Edit {{name}}', { name: editing.name })} className="max-w-lg">
          <ChannelForm channel={editing === 'new' ? null : editing} onDone={() => { setEditing(null); refresh(); }} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

function ChannelForm({ channel, onDone }: { channel: Channel | null; onDone: () => void }) {
  const [v, setV] = React.useState({
    name: channel?.name ?? '', type: channel?.type ?? 'EMAIL', target: channel?.target ?? '', secret: '',
    breach: channel ? channel.events.includes('sla.breach') : true, recovered: channel ? channel.events.includes('sla.recovered') : true,
    status: channel?.status ?? 'ACTIVE',
  });
  const queues = useRefOptions('queues');
  const [qids, setQids] = React.useState<string[]>(channel?.queue_ids ? channel.queue_ids.split(',') : []);
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const events = [v.breach && 'sla.breach', v.recovered && 'sla.recovered'].filter(Boolean);
    const body: Record<string, unknown> = { name: v.name, type: v.type, target: v.target, events, status: v.status, queue_ids: qids.map(Number) };
    if (v.type === 'WEBHOOK' && v.secret) body.secret = v.secret;
    try {
      if (channel) await api(`/notification-channels/${channel.id}`, { method: 'PATCH', body });
      else await api('/notification-channels', { body });
      toast.ok(t('Saved')); onDone();
    } catch (err) { setError(err); }
  }
  async function clearSecret() {
    if (!channel) return;
    try { await api(`/notification-channels/${channel.id}`, { method: 'PATCH', body: { secret: null } }); toast.ok(t('Secret removed')); onDone(); }
    catch (err) { setError(err); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      <Field help="notifications.name" label={t('Name')}><Input name="name" value={v.name} onChange={set('name')} maxLength={100} required /></Field>
      <Field help="notifications.type" label={t('Type')}>
        <Select name="type" value={v.type} onChange={set('type')}><option value="EMAIL">{t('E-mail')}</option><option value="WEBHOOK">{t('Webhook')}</option></Select>
      </Field>
      {v.type === 'EMAIL'
        ? <Field help="notifications.email_target" label={t('E-mail addresses')} hint={t('Separate several with commas.')}><Input name="target" value={v.target} onChange={set('target')} placeholder="ops@yourcompany.com" required /></Field>
        : <>
          <Field help="notifications.webhook_url" label={t('Webhook URL')} hint={t('Gets a POST with a JSON body for every notice.')}><Input name="target" value={v.target} onChange={set('target')} placeholder="https://hooks.yourcompany.com/sla" required /></Field>
          <Field help="notifications.secret" label={t('Signing secret')} hint={channel?.has_secret ? t('Set. Type a new one to replace it.') : t('Optional. Each request then carries X-CC-Signature: sha256=<HMAC of the body>.')}>
            <div className="flex gap-2">
              <Input name="secret" type="password" autoComplete="new-password" value={v.secret} onChange={set('secret')} maxLength={128} />
              {channel?.has_secret && <Button type="button" variant="outline" onClick={clearSecret}>{t('Remove')}</Button>}
            </div>
          </Field>
        </>}
      <div className="grid gap-1 text-sm">
        <span className="font-medium">{t('Send')}</span>
        <label className="flex items-center gap-2"><input type="checkbox" checked={v.breach} onChange={(e) => setV((x) => ({ ...x, breach: e.target.checked }))} /> {t('When a queue misses its SLA')}</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={v.recovered} onChange={(e) => setV((x) => ({ ...x, recovered: e.target.checked }))} /> {t('When it is back on target')}</label>
      </div>
      <div className="grid gap-1 text-sm" data-testid="channel-queues">
        <span className="font-medium">{t('Queues')}</span>
        <span className="text-xs text-muted-foreground">{t('None ticked: alerts of every queue.')}</span>
        <div className="grid max-h-40 grid-cols-2 gap-1 overflow-y-auto">
          {(queues.data ?? []).map((x) => (
            <label key={x.id} className="flex items-center gap-2">
              <input type="checkbox" checked={qids.includes(String(x.id))}
                onChange={(e) => setQids((l) => e.target.checked ? [...l, String(x.id)] : l.filter((y) => y !== String(x.id)))} /> {x.name}
            </label>
          ))}
        </div>
      </div>
      {channel && <Field help="notifications.status" label={t('Status')}><Select name="status" value={v.status} onChange={set('status')}><option value="ACTIVE">{t('Active')}</option><option value="DISABLED">{t('Off')}</option></Select></Field>}
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>

    </form>
  );
}
